// The feature vocabulary: every decision the role policy makes, by name.
// Client-safe — the web and mobile apps import this to type `useCanAccess` and
// to read the server-resolved list off `/api/auth/me`. The role → feature
// table itself lives in `./policy.ts` and is evaluated on the server only.
//
// docs/specs/2026-10-01-role-permissions-design.md, "Feature catalogue".

export const FEATURES = [
  'admin',
  'admin.trusstor',
  'cad.versions',
  'documents.approve',
  'documents.write',
  'schedule.edit',
  'schedule.pushToGenba',
  'tasks.execute',
  'tasks.contribute',
  'issues.manage',
  'issues.contribute',
  'annotations.write',
  'logistics.edit',
  'analytics.view',
  'portfolio.view',
  'reports.write',
  'safety.view',
  'safety.close',
  'comms.post',
  'comms.channels',
  'ai.use',
] as const;

export type Feature = (typeof FEATURES)[number];

const KNOWN: ReadonlySet<string> = new Set(FEATURES);

/**
 * Lenient read of a server-sent feature list: anything that isn't a known
 * feature name is dropped, never rejected. A newer server may grant a feature
 * an older client was built without — that must not break the client's login.
 */
export function parseFeatures(raw: unknown): Feature[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((value): value is Feature => typeof value === 'string' && KNOWN.has(value));
}
